// Step text (log, output) is read when a tab needs it, so these entries are only sometimes complete.
export type DetailState = "loading" | "loaded" | "error";

interface StepText {
  processLog?: string | null;
  output?: string | null;
}

interface StepEntry extends StepText {
  detailState?: DetailState;
  [key: string]: any;
}

/** Never read, read before a push made it stale, or a failed read; a read in flight is not repeated. */
export function needsStepDetail(entry?: StepEntry | null): boolean {
  return (
    !!entry && entry.detailState !== "loading" && entry.detailState !== "loaded"
  );
}

/** Adds the text read for a step; everything else stays as the summary gave it, verdict included. */
export function applyStepDetail(
  entry: StepEntry,
  detail?: StepText | null
): void {
  if (!detail) {
    entry.detailState = "error";
    return;
  }
  entry.processLog = detail.processLog;
  entry.output = detail.output;
  entry.detailState = "loaded";
}

/** A push carries the newest fragment only, so it keeps what the entry has and marks its text stale. */
export function mergeProgressMessage(
  entry: StepEntry,
  message: StepEntry
): StepEntry {
  const merged: StepEntry = { ...entry, ...message, detailState: undefined };
  if (!message.processLog || message.processLog.length === 0) {
    merged.processLog = entry.processLog;
  }
  return merged;
}

/** A read taken while the run was live is stale once it moves on, so it is read again. */
export function needsFlowLogs(
  state: DetailState | undefined,
  readWhileRunning: boolean
): boolean {
  return state !== "loading" && (state !== "loaded" || readWhileRunning);
}

/** The run's process logs in step order, as the flow Logs tab lays them out. */
export function concatProcessLogs(stepLogs?: StepText[] | null): string {
  return (stepLogs ?? []).map((stepLog) => stepLog.processLog ?? "").join("");
}
